export type MaturityKey = 'Instant' | '1W' | '2W' | '4W' | '12W' | '24W';
export type WeightingMethod = 'totalBorrowed' | 'totalSupply';

export interface OrderLevel {
  id: string;
  rate: number;
  size: number;
}

export interface InstantMarket {
  protocol: string;
  rate: number;
  totalSupply: number;
  totalBorrowed: number;
}

export interface MaturityData {
  maturity: MaturityKey;
  apr: number;
  totalLiquidity: number;
  totalBorrowDemand: number;
  supplyOrders: OrderLevel[];
  borrowOrders: OrderLevel[];
  instantMarkets?: InstantMarket[];
}

export const DEFAULT_INSTANT_MARKETS: InstantMarket[] = [
  { protocol: 'Aave', rate: 4.2, totalSupply: 120_000_000, totalBorrowed: 84_000_000 },
  { protocol: 'Morpho', rate: 4.5, totalSupply: 95_000_000, totalBorrowed: 61_000_000 },
  { protocol: 'Euler', rate: 4.1, totalSupply: 40_000_000, totalBorrowed: 22_000_000 },
];

export function calcInstantAPR(markets: InstantMarket[], method: WeightingMethod = 'totalBorrowed'): number {
  const totalWeight = markets.reduce((s, m) => s + (method === 'totalBorrowed' ? m.totalBorrowed : m.totalSupply), 0);
  if (totalWeight === 0) return 0;
  const weighted = markets.reduce((s, m) => {
    const w = method === 'totalBorrowed' ? m.totalBorrowed : m.totalSupply;
    return s + m.rate * w;
  }, 0);
  return weighted / totalWeight;
}

export const MATURITY_LABELS: MaturityKey[] = ['Instant', '1W', '2W', '4W', '12W', '24W'];

export const MATURITY_X_POSITIONS: Record<MaturityKey, number> = {
  Instant: 0,
  '1W': 1,
  '2W': 2,
  '4W': 3,
  '12W': 4,
  '24W': 5,
};

// Seeded initial data — replace with your values via the input panel
// Backend integration point: load from API GET /api/yield-curve/maturities
export const INITIAL_MATURITY_DATA: MaturityData[] = [
  {
    maturity: 'Instant',
    apr: calcInstantAPR(DEFAULT_INSTANT_MARKETS, 'totalBorrowed'),
    totalLiquidity: DEFAULT_INSTANT_MARKETS.reduce((s, m) => s + m.totalSupply, 0),
    totalBorrowDemand: DEFAULT_INSTANT_MARKETS.reduce((s, m) => s + m.totalBorrowed, 0),
    supplyOrders: [],
    borrowOrders: [],
    instantMarkets: DEFAULT_INSTANT_MARKETS,
  },
  {
    maturity: '1W',
    apr: 4.6,
    totalLiquidity: 7_500_000,
    totalBorrowDemand: 5_200_000,
    supplyOrders: [
      { id: 's1', rate: 4.2, size: 1_000_000 },
      { id: 's2', rate: 4.4, size: 2_000_000 },
      { id: 's3', rate: 4.6, size: 2_500_000 },
      { id: 's4', rate: 4.8, size: 1_200_000 },
      { id: 's5', rate: 5.0, size: 800_000 },
    ],
    borrowOrders: [
      { id: 'b1', rate: 4.6, size: 1_500_000 },
      { id: 'b2', rate: 4.8, size: 2_000_000 },
      { id: 'b3', rate: 5.0, size: 1_200_000 },
      { id: 'b4', rate: 5.2, size: 500_000 },
    ],
  },
  {
    maturity: '2W',
    apr: 5.1,
    totalLiquidity: 11_000_000,
    totalBorrowDemand: 7_800_000,
    supplyOrders: [
      { id: 's1', rate: 4.8, size: 1_500_000 },
      { id: 's2', rate: 5.0, size: 2_500_000 },
      { id: 's3', rate: 5.2, size: 3_500_000 },
      { id: 's4', rate: 5.4, size: 2_000_000 },
      { id: 's5', rate: 5.6, size: 1_500_000 },
    ],
    borrowOrders: [
      { id: 'b1', rate: 5.1, size: 2_000_000 },
      { id: 'b2', rate: 5.3, size: 2_800_000 },
      { id: 'b3', rate: 5.5, size: 2_000_000 },
      { id: 'b4', rate: 5.7, size: 1_000_000 },
    ],
  },
  {
    maturity: '4W',
    apr: 6.0,
    totalLiquidity: 18_500_000,
    totalBorrowDemand: 14_200_000,
    supplyOrders: [
      { id: 's1', rate: 5.5, size: 2_000_000 },
      { id: 's2', rate: 5.7, size: 3_500_000 },
      { id: 's3', rate: 5.9, size: 5_000_000 },
      { id: 's4', rate: 6.1, size: 4_000_000 },
      { id: 's5', rate: 6.3, size: 3_000_000 },
      { id: 's6', rate: 6.5, size: 1_000_000 },
    ],
    borrowOrders: [
      { id: 'b1', rate: 6.0, size: 2_500_000 },
      { id: 'b2', rate: 6.2, size: 4_000_000 },
      { id: 'b3', rate: 6.4, size: 4_500_000 },
      { id: 'b4', rate: 6.6, size: 2_200_000 },
      { id: 'b5', rate: 6.8, size: 1_000_000 },
    ],
  },
  {
    maturity: '12W',
    apr: 7.4,
    totalLiquidity: 24_000_000,
    totalBorrowDemand: 20_500_000,
    supplyOrders: [
      { id: 's1', rate: 6.8, size: 3_000_000 },
      { id: 's2', rate: 7.0, size: 5_000_000 },
      { id: 's3', rate: 7.2, size: 6_500_000 },
      { id: 's4', rate: 7.4, size: 5_500_000 },
      { id: 's5', rate: 7.6, size: 3_000_000 },
      { id: 's6', rate: 7.8, size: 1_000_000 },
    ],
    borrowOrders: [
      { id: 'b1', rate: 7.4, size: 3_500_000 },
      { id: 'b2', rate: 7.6, size: 5_500_000 },
      { id: 'b3', rate: 7.8, size: 6_000_000 },
      { id: 'b4', rate: 8.0, size: 4_000_000 },
      { id: 'b5', rate: 8.2, size: 1_500_000 },
    ],
  },
  {
    maturity: '24W',
    apr: 8.9,
    totalLiquidity: 31_000_000,
    totalBorrowDemand: 28_500_000,
    supplyOrders: [
      { id: 's1', rate: 8.2, size: 4_000_000 },
      { id: 's2', rate: 8.5, size: 7_000_000 },
      { id: 's3', rate: 8.8, size: 9_000_000 },
      { id: 's4', rate: 9.1, size: 6_500_000 },
      { id: 's5', rate: 9.4, size: 3_500_000 },
      { id: 's6', rate: 9.7, size: 1_000_000 },
    ],
    borrowOrders: [
      { id: 'b1', rate: 8.9, size: 5_000_000 },
      { id: 'b2', rate: 9.1, size: 7_500_000 },
      { id: 'b3', rate: 9.3, size: 8_000_000 },
      { id: 'b4', rate: 9.5, size: 5_500_000 },
      { id: 'b5', rate: 9.7, size: 2_500_000 },
    ],
  },
];